import { WebrtcSIPPhone } from "@exotel-npm-dev/webrtc-core-sdk";
import api from "@/lib/api";

/**
 * Exotel browser-calling engine — mirrors the twilioDevice interface so the
 * shared softphone UI works unchanged when the active provider is "exotel".
 *
 * Model: the agent's browser REGISTERS its SIP identity (auto-provisioned by
 * the backend). Outbound calls are originated by the BACKEND connect API, which
 * rings this registered device (an incoming SIP leg) — we auto-accept it, and
 * audio flows. So `connect()` here just guarantees registration; the backend
 * has already placed the bridged call.
 */
class ExotelDeviceManager {
    constructor() {
        this.phone = null;
        this.registered = false;
        this.identity = null;
        this.autoAccept = true; // bridged agent leg should answer automatically
        this.listeners = new Set();
        this._ensuring = null;
    }

    on(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
    emit(event, payload = {}) { for (const fn of this.listeners) fn({ event, ...payload }); }

    async ensureDevice() {
        if (this.registered && this.phone) return this.phone;
        if (this._ensuring) return this._ensuring;
        this._ensuring = this._register();
        try { return await this._ensuring; }
        finally { this._ensuring = null; }
    }

    async _register() {
        const { data: cfg } = await api.get("/exotel/session");
        console.log("[exotelDevice] session cfg:", { ...cfg, sip_password: "***" });
        this.identity = cfg.sip_username;

        const sipAccountInfo = {
            userName: cfg.account_name || cfg.sip_username,
            authUser: cfg.sip_username,
            secret: cfg.sip_password,
            sipdomain: cfg.sip_domain,
            // WebSocket gateway: Exotel's proxy host resolves (voip.exotel.com);
            // the SIP domain is the realm, not the WS host. Overridable via ws_host.
            domain: cfg.ws_host || cfg.proxy,
            accountName: cfg.account_name,
            security: cfg.security || "wss",
        };
        if (cfg.ws_endpoint !== undefined) sipAccountInfo.endpoint = cfg.ws_endpoint;

        const phone = new WebrtcSIPPhone(cfg.sip_username);
        const delegate = {
            sendWebRTCEventsToFSM: (evType, method) => {
                console.log("[exotelDevice] FSM", method, evType);
                if (method === "CONNECTION" && (evType === "registered" || evType === "connected")) {
                    this.registered = true;
                    this.emit("registered");
                } else if (method === "CONNECTION" && (evType === "unregistered" || evType === "terminated" || evType === "disconnected")) {
                    this.registered = false;
                    this.emit("unregistered");
                } else if (method === "CALL" && evType === "i_new_call") {
                    // Incoming bridged agent leg from Exotel — auto-answer it.
                    console.log("[exotelDevice] 🔔 incoming leg (i_new_call) — auto-accepting");
                    if (this.autoAccept) { try { phone.pickCall(); } catch (e) { console.error(e); } }
                    this.emit("incoming", { autoAccepted: this.autoAccept });
                } else if (method === "CALL" && evType === "m_permission_refused") {
                    this.emit("error", { error: "Microphone permission is required to answer the call." });
                } else if (method === "CALL" && evType === "connected") {
                    this.emit("call:accept", { inbound: true });
                } else if (method === "CALL" && (evType === "terminated" || evType === "disconnected")) {
                    this.emit("call:disconnect");
                }
            },
            onRecieveInvite: () => {
                console.log("[exotelDevice] 🔔 incoming leg (bridged) — auto-accepting");
                if (this.autoAccept) { try { phone.pickCall(); } catch (e) { console.error(e); } }
                this.emit("incoming", { autoAccepted: this.autoAccept });
            },
        };

        return await new Promise((resolve, reject) => {
            try {
                phone.registerPhone("sipjs", delegate, true);
                phone.registerWebRTCClient(sipAccountInfo, {
                    onResponse: (state) => {
                        console.log("[exotelDevice] onResponse:", state);
                        if (state === "ready") { this.registered = true; this.emit("registered"); resolve(phone); }
                        else if (state === "connected") { this.emit("call:accept"); }
                        else if (state === "disconnected") { this.emit("call:disconnect"); }
                    },
                    onFailure: () => {
                        console.error("[exotelDevice] ❌ registration failed");
                        this.registered = false;
                        this.emit("error", { error: "Exotel SIP registration failed" });
                        reject(new Error("Exotel SIP registration failed"));
                    },
                });
                this.phone = phone;
                // Safety: resolve once registered even if onResponse timing differs.
                setTimeout(() => { if (this.registered) resolve(phone); }, 6000);
            } catch (e) {
                console.error("[exotelDevice] register threw", e);
                reject(e);
            }
        });
    }

    /** Backend already originated the bridged connect call; just ensure we're online. */
    async connect() {
        await this.primeAudio();
        await this.ensureDevice();
        return true;
    }

    disconnect() { try { this.phone?.disconnect(); } catch (_e) {} }
    mute(muted) { try { this.phone?.muteAction(!!muted); } catch (_e) {} }
    acceptIncoming() { try { this.phone?.pickCall(); } catch (_e) {} }
    rejectIncoming() { try { this.phone?.rejectCall(); } catch (_e) {} }
    setAutoAccept(v) { this.autoAccept = !!v; }
    getActiveCallSid() { return null; }
    requestDial(number, opts = {}) { this.emit("dialer:open", { number, autoDial: !!opts.autoDial, campaign: !!opts.campaign }); }
    async primeAudio() {
        // Request microphone permission up-front so the browser can answer Exotel's
        // incoming INVITE immediately (otherwise the agent leg is 'from_leg_unanswered').
        try {
            if (navigator?.mediaDevices?.getUserMedia) {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                stream.getTracks().forEach((t) => t.stop());
            }
        } catch (e) {
            this.emit("error", { error: "Microphone permission is required to make Exotel browser calls." });
            throw new Error("Microphone permission denied/unavailable");
        }
    }
    async reinit() { this.registered = false; this.phone = null; return this.ensureDevice(); }
    isReady() { return !!this.phone && this.registered; }
    debugState() { return { identity: this.identity, registered: this.registered }; }
}

const exotelDevice = new ExotelDeviceManager();
if (typeof window !== "undefined") window.exotelDevice = exotelDevice;
export default exotelDevice;
